import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import LandingHeader from "../LandingHeader";
import Footer from "../../../components/Footer/Footer";

const ContactPage: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Email and message are required
    if (!formData.email.trim() || !formData.message.trim()) {
      alert("Please fill in both Email and Message fields.");
      return;
    }

    console.log("Form submitted:", formData);
    setOpen(true);
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <Box
      sx={{
        fontFamily: '"Lexend", sans-serif',
        backgroundColor: "#f9f9f9",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {/* ─── Top Navigation ─── */}
      <LandingHeader />

      {/* ─── Main Contact Section ─── */}
      <Box component="main" sx={{ flex: 1, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={700}
            textAlign="center"
            gutterBottom
            sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" } }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            mb={5}
            sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.8 }}
          >
            We'd love to hear from you. Whether it's a question, feedback, or a
            request — fill out the form and we’ll respond as soon as we can.
          </Typography>

          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              px: { xs: 3, sm: 5 },
              py: { xs: 4, sm: 5 },
              boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  inputProps={{ style: { fontFamily: "Lexend" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  inputProps={{ style: { fontFamily: "Lexend" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  inputProps={{ style: { fontFamily: "Lexend" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message"
                  name="message"
                  multiline
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  inputProps={{ style: { fontFamily: "Lexend" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    fontFamily: '"Lexend", sans-serif',
                    textTransform: "none",
                    fontSize: "1rem",
                    background:
                      "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
                    color: "#fff",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #4F46E5 0%, #8B2EFF 100%)",
                    },
                  }}
                >
                  Send Message
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ─── Footer ─── */}
      <Footer />

      {/* ─── Success Snackbar ─── */}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="filled"
          sx={{ fontFamily: '"Lexend", sans-serif' }}
        >
          Your message has been sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;